import { useQuery } from "@tanstack/react-query";
import { type Api, getAthleteAssignedProgramHistory } from "api-client";
import { useMemo } from "react";

import { useProgramBuilderContext } from "src/contexts/ProgramBuilderContext";

// Query key factory — keep consistent across read sites and
// invalidations. Hierarchical so `["programs", "history"]` invalidates
// every program's history with one call.
export function programHistoryKey(programId: string) {
  return ["programs", "history", { programId }] as const;
}

interface UseProgramHistoryOptions {
  enabled?: boolean;
  // `'always'` makes the observer refetch every time it mounts —
  // intended for the route-level mount (program grid) so opening the
  // route always shows the freshest history. Dialog-level consumers use
  // the default (`false`) so reopening dialogs reads from the cache
  // populated by the route's mount.
  refetchOnMount?: false | "always";
}

type HistoryEntry =
  Api.GetAthleteAssignedProgramHistory.Output.Self["history"][number];
type SubmissionEntry =
  Api.GetAthleteAssignedProgramHistory.Output.Self["questionnaireSubmissions"][number];

// Single source of truth for fetching per-program exercise history +
// questionnaire submissions. Server returns flat lists; we build
// id-keyed lookups here so consumers can read in O(1).
export function useProgramHistory(
  programId: string,
  options: UseProgramHistoryOptions = {},
) {
  const query = useQuery({
    queryKey: programHistoryKey(programId),
    queryFn: async () => {
      const res = await getAthleteAssignedProgramHistory({
        path: { id: programId },
      });

      return res.data.data;
    },
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnMount: options.refetchOnMount ?? false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const lookups = useMemo(() => {
    const sessionsByExerciseId: Record<string, HistoryEntry["sessions"]> = {};
    const submissionByWorkoutId: Record<string, SubmissionEntry> = {};

    if (query.data) {
      for (const entry of query.data.history) {
        sessionsByExerciseId[entry.exerciseId] = entry.sessions;
      }

      for (const entry of query.data.questionnaireSubmissions) {
        submissionByWorkoutId[entry.baseWorkoutId] = entry;
      }
    }

    return { sessionsByExerciseId, submissionByWorkoutId };
  }, [query.data]);

  return { ...query, lookups };
}

// Resolves the active program id from the builder context, if any.
// Components like WorkoutItemSets are also rendered outside of a
// program (calendar modal, section editor) where there is no history
// to fetch — in that case the underlying query stays disabled.
function useActiveProgramId(): string | null {
  const programBuilderContext = useProgramBuilderContext();
  return programBuilderContext?.store.program.id ?? null;
}

// Convenience derived hook for a single exercise's history sessions.
export function useWorkoutItemHistory(
  exerciseId: string | undefined,
): HistoryEntry["sessions"] {
  const programId = useActiveProgramId();
  const { lookups } = useProgramHistory(programId ?? "", {
    enabled: !!programId,
  });

  if (!exerciseId) return [];

  return lookups.sessionsByExerciseId[exerciseId] ?? [];
}

// Convenience derived hook for a single workout's questionnaire submission.
export function useQuestionnaireSubmissionForWorkout(
  baseWorkoutId: string | undefined,
): SubmissionEntry | null {
  const programId = useActiveProgramId();
  const { lookups } = useProgramHistory(programId ?? "", {
    enabled: !!programId,
  });

  if (!baseWorkoutId) return null;

  return lookups.submissionByWorkoutId[baseWorkoutId] ?? null;
}
